
import { Component, Mixins } from 'vue-property-decorator'
import { TheMask } from 'vue-the-mask'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import PaymentMixin from '~/components/common/modals/payment/PaymentMixin'
import Icon from '~/components/common/icon/Icon.vue'
import ExpirationDate from '~/components/common/modals/payment/ExpirationDate.vue'

interface BTPayloadDetails {
  bin: string;
  lastTwo: string;
  lastFour: string;
  cardType: string;
  cardholderName: string;
  expirationYear: string;
  expirationMonth: string;
}

interface BTPayload {
  nonce: string;
  details: BTPayloadDetails
  type: string;
}

@Component({
  components: {
    Icon,
    ExpirationDate,
    ValidationProvider,
    ValidationObserver,
    TheMask
  }
})

export default class PaymentForm extends Mixins(PaymentMixin) {
  cardNumber = ''
  cvv = ''
  expirationDate = { month: '', year: '' }

  btNonce = ''
  deviceData = null

  integration = {
    bt: null
  }

  serverError = ''
  isSending = false
  isPaypalInitializing = false

  mounted() {
    this.init()
  }

  beforeDestroy() {
    this.teardownIntegration()
  }

  get months() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return Array.from({ length: 12 }, (v, i) => (i > 8 ? (i + 1).toString() : '0' + (i + 1)))
  }

  get years() {
    const date = new Date()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return Array.from({ length: 20 }, (v, i) => (date.getFullYear() + i).toString())
  }

  init() {
    this.$accessor.payment.getToken()
      .then((token: string) => {
        if (!this.isBraintreeDefined()) { return }
        this.initCardPayment(token)
      })

    this.initPayPal({
      success: this.paymentSuccessHandler,
      fail: this.errorHandler
    })
  }

  isBraintreeDefined() {
    if (!window.braintree) {
      this.$sentry.captureException(new Error('Braintree instance is not defined'))
    }

    return !!window.braintree
  }

  setIntegration(name, integration) {
    if (name in this.integration) {
      this.integration[name] = integration
    }
  }

  teardownIntegration() {
    for (const key of Object.keys(this.integration)) {
      if (!this.integration[key]) { continue }

      this.integration[key].teardown(() => this.setIntegration(key, null))
    }

    this.teardownPayPal()
  }

  initCardPayment(token) {
    window.braintree.setup(token, 'custom', {
      id: (this.$refs.form as HTMLFormElement).id,
      onReady: integration => this.setIntegration('bt', integration),
      onError: this.errorHandler,
      onPaymentMethodReceived: (payload: BTPayload) => {
        this.fillPayloadAttributes(payload)
        this.paymentRequest()
      }
    })
  }

  fillPayloadAttributes(payload: BTPayload) {
    this.btNonce = payload.nonce
    const input = (this.$refs.form as HTMLFormElement).querySelector('[name=payment_method_nonce]')
    if (input) { input.setAttribute('value', payload.nonce) }
  }

  paymentRequest() {
    const form = new FormData(this.$refs.form as HTMLFormElement)
    return this.$api.payment.sendTokenizedData(form)
      .then((res) => {
        this.isSending = false
        this.paymentSuccessHandler(res.data.data)
        return res.data.data.message
      })
      .catch(err => this.showError(err))
      .finally(() => (this.isSending = false))
  }

  submit(ev, validate) {
    if (this.serverError) {
      this.serverError = ''
    }

    if (this.isSending) {
      ev.preventDefault()
      return
    }

    validate().then((isValid) => {
      if (!isValid) {
        ev.preventDefault()
        return
      }

      this.isSending = true
    })
  }

  showError(err) {
    this.$sentry.captureException(err)
    if (err.response.data && err.response.data.error) {
      this.serverError = err.response.data.error
    }
  }
}
